import { cloneDeep } from "lodash";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useOutletContext } from "react-router-dom";
import styled from "styled-components";
import { useFilePicker } from "use-file-picker";
import {
  FileAmountLimitValidator,
  FileTypeValidator,
  FileSizeValidator,
  ImageDimensionsValidator,
} from "use-file-picker/validators";

// Imported assets
import DefaultAvatar from "../../../../assets/icons/ProfilePicForNavBar.svg";
import EditIcon from "../../../../assets/icons/Edit_WhiteIcon.svg";
import PrimaryButton from "../../../../components/ui/PrimaryButton";

// TODO: Remove temp avatars for demos
import lgAvatar from "../../../../assets/temp/lg-demo-avatar.png";
import tonyWaltersAvatar from "../../../../assets/temp/tony-k-walters.png";

// Imported components
import StandardLabel from "../../../../components/form/Label";

// Imported contexts
import { useUserContext } from "../../../../contexts/User";
import { getUser } from "../../../../actions/user";
import { isLoggedIn } from "../../../../utils/user";

// Styled components
const AvatarContainer = styled.div`
  border-radius: 50%;
`;

const ButtonContainer = styled.div`
  align-content: flex-end;
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: row;
  gap: 20px;
  justify-content: center;

  & > button {
    height: 40px;
    min-width: 160px;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  // gap: 20px;
  height: 100%;
  width: 100%;
`;

const FormField = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 4px;
  justify-content: flex-start;
  position: relative;

  & .MuiInputBase-root {
    background: #ffffff;
    height: 40px;
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  gap: 20px;
  margin: 20px 0 0 20px;
`;

const HeaderBox = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: 5px;
  justify-content: space-evenly;
`;

const HeaderButton = styled(PrimaryButton)`
  display: flex;
  flex-direction: row;
  font-family: Inter, Oxygen, Roboto, sans-serif;
  font-size: 14px;
  height: 32px;
  min-width: 140px;
  width: auto;
`;

const HeaderControls = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: row;
  gap: 12px;
`;

const HeaderText = styled.span`
  align-content: center;
  font-family: Oxygen, Inter, Roboto, sans-serif;
  font-size: 36px;
  text-align: center;
`;

const Icon = styled.img`
  height: auto;
  margin-right: 10px;
  width: 20px;

  &:hover {
    cursor: pointer;
  }
`;

const InfoRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 20px;

  & * {
    font-family: Inter, Oxygen, Roboto, sans-serif !important;
  }
`;

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin: 20px 20px;
`;

const Label = styled(StandardLabel)`
  font-family: Inter, Oxygen, Roboto, sans-serif;
  font-size: 16px;
  font-weight: 400;
`;

const ResetButton = styled(HeaderButton)`
  background: white;
  border: 1px solid #d9d9d9;
  color: black;
  font-family: Inter, Oxygen, Roboto, sans-serif;

  &:hover {
    background: lab(from white calc(l - 5) a b / 100);
  }
`;

const ThemeButton = styled(HeaderButton)`
  background: var(--terbine-blue);
  font-family: Inter, Oxygen, Roboto, sans-serif;

  &:hover {
    background: lab(from var(--terbine-blue) calc(l - 5) a b / 100);
  }
`;

const UpdateButton = styled(PrimaryButton)`
  background: var(--terbine-blue);
  display: flex;
  font-family: Inter, Oxygen, Roboto, sans-serif;
  flex-direction: row;
  height: 40px;
  width: auto;

  &:hover {
    background: lab(from var(--terbine-blue) calc(l - 5) a b / 100);
  }
`;

const UserAvatar = styled.img`
  height: auto;
  width: 80px;
`;

const ProfileInfo = () => {
  // Image picker for user's avatar
  const { openFilePicker, filesContent, loading, errors } = useFilePicker({
    accept: "image/*",
    multiple: false,
    readAs: "DataURL",
    validators: [
      new FileAmountLimitValidator({ max: 1 }),
      new FileTypeValidator(["jpg", "png"]),
      new FileSizeValidator({ maxFileSize: 350 * 1024 /* 350 KB */ }),
      new ImageDimensionsValidator({
        maxHeight: 400, // in pixels
        maxWidth: 400,
        // minHeight: 400,
        // minWidth: 400,
      }),
    ],
    onFilesSelected: ({ plainFiles, filesContent, errors }) => {
      // this callback is always called, even if there are errors
      console.log("onFilesSelected", plainFiles, filesContent, errors);
    },
    onFilesRejected: ({ errors }) => {
      // this callback is called when there were validation errors
      console.log("onFilesRejected", errors);
    },
    onFilesSuccessfullySelected: ({ plainFiles, filesContent }) => {
      // this callback is called when there were no validation errors
      console.log("onFilesSuccessfullySelected", plainFiles, filesContent);
    },
  });

  const appLocation = useLocation();
  const navigate = useNavigate();

  const { updateUserData, user } = useUserContext();
  const { usStates } = useOutletContext();

  const [userData, setUserData] = useState();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  const [firstNameErr, setFirstNameErr] = useState(null);
  const [lastNameErr, setLastNameErr] = useState(null);
  const [emailErr, setEmailErr] = useState(null);
  const [phoneErr, setPhoneErr] = useState(null);

  const [inEditMode, setInEditMode] = useState(false);

  useEffect(() => {
    const getUserData = async () => {
      await getUser(user.id)
        .then(async (userData) => {
          setUserData(userData);
        })
        .catch((err) => {
          // handle error
        });
    };

    if (!isLoggedIn()) {
      navigate("/login", {
        replace: true,
        state: { path: appLocation.pathname },
      });
    } else getUserData();
  }, [user]);

  useEffect(() => {
    setFirstName(userData?.missionControlUser.firstName);
    setLastName(userData?.missionControlUser.lastName);
    setEmail(userData?.emails.find((e) => e.isPrimary).emailAddress);
    setPhone(userData?.phones.find((p) => p.isPrimary).phoneNumber);
  }, [userData]);

  const resetForm = (e) => {
    e?.preventDefault();

    setFirstName(userData?.missionControlUser.firstName);
    setLastName(userData?.missionControlUser.lastName);
    setEmail(userData?.emails.find((e) => e.isPrimary).emailAddress);
    setPhone(userData?.phones.find((p) => p.isPrimary).phoneNumber);

    setFirstNameErr(null);
    setLastNameErr(null);
    setEmailErr(null);
    setPhoneErr(null);
  };

  const handleUpdateUserData = () => {
    // Create payload object to pass along to updateUserData user context function
    let payload = {
      ...cloneDeep(userData.missionControlUser),
      firstName: firstName,
      lastName: lastName,
      emails: cloneDeep(userData.emails),
      addresses: cloneDeep(userData.addresses),
      phones: cloneDeep(userData.phones),
    };

    // Replace primary email with new email
    let primaryEmail = cloneDeep(payload.emails.find((e) => e.isPrimary));
    primaryEmail.emailAddress = email;
    payload.emails.splice(
      payload.emails.findIndex((e) => e.isPrimary),
      1,
      cloneDeep(primaryEmail)
    );

    // Replace primary phone with new phone
    let primaryPhone = cloneDeep(payload.phones.find((p) => p.isPrimary));
    primaryPhone.phoneNumber = phone;
    payload.phones.splice(
      payload.phones.findIndex((p) => p.isPrimary),
      1,
      cloneDeep(primaryPhone)
    );

    console.log(payload);

    updateUserData(payload);
    // updateUserData(firstName, lastName, email, phone);
  };

  return (
    <Container>
      <Header>
        <AvatarContainer>
          <UserAvatar
            src={
              user.userId === "joseph.d.scott@lge.com"
                ? lgAvatar
                : user.userId === "tony.k.walters@cnmp.com"
                ? tonyWaltersAvatar
                : DefaultAvatar
            }
          />
        </AvatarContainer>
        {!inEditMode && <HeaderText>{user.displayName}</HeaderText>}

        {inEditMode && (
          <>
            <HeaderBox>
              <HeaderText>{user.displayName}</HeaderText>
              <HeaderControls>
                <ThemeButton onClick={() => openFilePicker()} type="button">
                  Upload New Photo
                </ThemeButton>
                <ResetButton onClick={(e) => resetForm(e)} type="button">
                  Reset
                </ResetButton>
              </HeaderControls>
            </HeaderBox>
          </>
        )}
      </Header>

      <InfoSection>
        {!inEditMode && (
          <>
            <InfoRow>
              <FormField style={{ flex: 1 }}>
                <FormControl sx={{ width: "100%" }}>
                  <Label>First Name</Label>
                  <TextField
                    disabled
                    value={userData?.missionControlUser.firstName}
                  />
                </FormControl>
              </FormField>

              <FormField style={{ flex: 1 }}>
                <FormControl sx={{ width: "100%" }}>
                  <Label>Last Name</Label>
                  <TextField
                    disabled
                    value={userData?.missionControlUser.lastName}
                  />
                </FormControl>
              </FormField>
            </InfoRow>

            <InfoRow>
              <FormField style={{ flex: 1 }}>
                <FormControl sx={{ width: "100%" }}>
                  <Label>Role(s)</Label>
                  <TextField
                    disabled
                    value={user.roles.map((r) => r.type.codeName).sort()}
                  />
                </FormControl>
              </FormField>

              {!user.roles
                .map((r) => r.type.codeName)
                .includes("Administrator") && (
                <FormField style={{ flex: 1 }}>
                  <FormControl sx={{ width: "100%" }}>
                    <Label>Assigned Region</Label>
                    <TextField
                      disabled
                      value={usStates.map((s) => s.name).join(", ")}
                    />
                  </FormControl>
                </FormField>
              )}
            </InfoRow>

            <InfoRow>
              <FormField style={{ flex: 1 }}>
                <FormControl sx={{ width: "100%" }}>
                  <Label>Email</Label>
                  <TextField
                    disabled
                    value={
                      userData?.emails.find((e) => e.isPrimary).emailAddress
                    }
                  />
                </FormControl>
              </FormField>
            </InfoRow>

            <InfoRow>
              <FormField style={{ flex: 1 }}>
                <FormControl sx={{ width: "100%" }}>
                  <Label>Phone Number</Label>
                  <TextField
                    disabled
                    value={
                      userData?.phones.find((p) => p.isPrimary).phoneNumber
                    }
                  />
                </FormControl>
              </FormField>
            </InfoRow>

            <InfoRow style={{ justifyContent: "flex-end" }}>
              <ButtonContainer>
                <UpdateButton onClick={() => setInEditMode(true)} type="button">
                  <Icon src={EditIcon} />
                  Update Profile
                </UpdateButton>
              </ButtonContainer>
            </InfoRow>
          </>
        )}

        {inEditMode && (
          <>
            <InfoRow>
              <FormField style={{ flex: 1 }}>
                <FormControl sx={{ width: "100%" }}>
                  <Label>First Name</Label>
                  <TextField
                    error={firstNameErr ? true : false}
                    onChange={(e) => setFirstName(e.target.value)}
                    value={firstName}
                    variant="outlined"
                  />
                </FormControl>
              </FormField>

              <FormField style={{ flex: 1 }}>
                <FormControl sx={{ width: "100%" }}>
                  <Label>Last Name</Label>
                  <TextField
                    error={lastNameErr ? true : false}
                    onChange={(e) => setLastName(e.target.value)}
                    value={lastName}
                    variant="outlined"
                  />
                </FormControl>
              </FormField>
            </InfoRow>

            <InfoRow>
              <FormField style={{ flex: 1 }}>
                <FormControl sx={{ width: "100%" }}>
                  <Label>Email</Label>
                  <TextField
                    error={emailErr ? true : false}
                    onChange={(e) => setEmail(e.target.value)}
                    // slotProps={{
                    //   htmlInput: {
                    //     maxLength: emailMax,
                    //   },
                    // }}
                    value={email}
                    variant="outlined"
                  />
                </FormControl>
              </FormField>
            </InfoRow>

            <InfoRow>
              <FormField style={{ flex: 1 }}>
                <FormControl sx={{ width: "100%" }}>
                  <Label>Phone Number</Label>
                  <TextField
                    error={phoneErr ? true : false}
                    onChange={(e) => setPhone(e.target.value)}
                    value={phone}
                    variant="outlined"
                  />
                </FormControl>
              </FormField>
            </InfoRow>

            <ButtonGroup>
              <ThemeButton
                onClick={() => {
                  handleUpdateUserData();
                  setInEditMode(false);
                }}
                type="button"
              >
                Save Changes
              </ThemeButton>

              <ResetButton
                onClick={(e) => {
                  resetForm(e);
                  setInEditMode(false);
                }}
                type="button"
              >
                Cancel
              </ResetButton>
            </ButtonGroup>
          </>
        )}
      </InfoSection>
    </Container>
  );
};

export default ProfileInfo;
